// File: Alert.jsx

import React from "react";
import PropTypes from "prop-types";
import { head, includes, trim, template, values } from "lodash";

import LoadingState from "@/components/items-list/components/LoadingState";
import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";

import { currentUser } from "@/services/auth";
import notification from "@/services/notification";
import AlertService from "@/services/alert";

import { AlertEvent } from "@/services/alert-events";

import { Query as QueryService } from "@/services/query";
import routes from "@/services/routes";

import MenuButton from "../../components/alerts/MenuButton";
import AlertView from "../../components/alerts/AlertView";
import AlertEdit from "../../components/alerts/AlertEdit";
import AlertNew from "../../components/alerts/AlertNew";
import AlertFevents from "../../components/alerts/AlertFevents";
import AlertStats from "../../components/alerts/AlertStats.jsx";

const MODES = {
  NEW: 0,
  VIEW: 1,
  EDIT: 2,
  FEVENTS: 3,
  STATS: 4,
  OVERVIEW: 5,
};

const defaultNameBuilder = template("<%= query.name %>: <%= options.column %> <%= options.op %> <%= options.value %>");

export function getDefaultName(alert) {
  if (!alert.query) {
    return "New Alert";
  }
  return defaultNameBuilder(alert);
}

class Alert extends React.Component {
  static propTypes = {
    mode: PropTypes.oneOf(values(MODES)),
    alertId: PropTypes.string,
    onError: PropTypes.func,
  };

  static defaultProps = {
    mode: null,
    alertId: null,
    onError: () => {},
  };

  _isMounted = false;

  state = {
    alert: null,
    queryResult: null,
    pendingRearm: null,
    canEdit: false,
    mode: null,
    events: null,
  };

  componentDidMount() {
    this._isMounted = true;
    const { mode, alertId } = this.props; // Get the alertId from props
    this.setState({ mode });

    if (mode === MODES.NEW) {
      this.setState({
        alert: {
          options: {
            op: ">",
            value: 1,
            muted: false,
          },
        },
        pendingRearm: 0,
        canEdit: true,
        is_archived: false,
        is_favorite: false,
        tags: [],
        rearm: 0,

      });
    } else {
      AlertService.get({ id: alertId }) // Use alertId to fetch alert details
        .then(alert => {
          if (this._isMounted) {
            const canEdit = currentUser.canEdit(alert);

            // force view mode if can't edit
            if (!canEdit) {
              this.setState({ mode: MODES.OVERVIEW });
              notification.warn(
                "You cannot edit this alert",
                "You do not have sufficient permissions to edit this alert, and have been redirected to the view-only page.",
                { duration: 0 }
              );
            }

            this.setState({ alert, canEdit, pendingRearm: alert.rearm, is_favorite: alert.is_favorite , tags: alert.tags});
            this.onQuerySelected(alert.query);

            // Fetch events data if in FEVENTS mode OR STATS
            if (mode === MODES.FEVENTS || mode === MODES.STATS || mode === MODES.OVERVIEW) {
              this.fetchEvents(alertId); // Fetch events using alertId
            }
          }
        })
        .catch(error => {
          if (this._isMounted) {
            this.props.onError(error);
          }
        });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchEvents(alertId) {
    AlertEvent.get_from_alert_id({ id: alertId }) // Use AlertEvent service to fetch events
      .then(events => {
        if (this._isMounted) {
          // Attach a favorite property to each event
          const eventsWithFavorite = events.map(event => ({
            ...event,
            is_favorite: event.is_favorite,
            favorite : () => !event.is_favorite ? this.favorite_event(event.id) : this.unfavorite_event(event.id),
            unfavorite : () => this.unfavorite_event(event.id),
            tags: event.tags,
            query: event.query,
            alert: event.alert,
          }));
          this.setState({ events: eventsWithFavorite });
        }
      })
      .catch(() => {
        notification.error("Failed to load alert events.");
      });
  }

  save = () => {
    const { alert, pendingRearm } = this.state;
    alert.name = trim(alert.name) || getDefaultName(alert);
    alert.rearm = pendingRearm || null;

    return AlertService.save(alert)
      .then(alert => {
        notification.success("Saved.");
        this.setState({ alert, mode: MODES.OVERVIEW });
        navigateTo(`alerts/${alert.id}/overview`, true);
      })
      .catch(() => {
        notification.error("Failed saving alert.");
      });
  };


  onQuerySelected = query => {
    this.setState(({ alert }) => ({
      alert: Object.assign(alert, { query }),
      queryResult: null, 
    }));

    if (query) {
      // get cached result for column names and values
      new QueryService(query).getQueryResultPromise().then(queryResult => {
        if (this._isMounted) {
          this.setState({ queryResult });
          let { column } = this.state.alert.options;
          const columns = queryResult.getColumnNames();

          // default to first column name if none chosen, or irrelevant in current query
          if (!column || !includes(columns, column)) {
            column = head(queryResult.getColumnNames());
          }
          this.setAlertOptions({ column });
        }
      });
    }
  };

  onNameChange = name => {
    const { alert } = this.state;
    this.setState({
      alert: Object.assign(alert, { name }),
    });
  };

  onRearmChange = pendingRearm => {
    this.setState({ pendingRearm });
  };

  setAlertOptions = (obj) => {
    const { alert } = this.state;
    const options = { ...alert.options, ...obj };
    this.setState({
      alert: Object.assign(alert, { options }),
    });
  };


  delete = () => {
    const { alert } = this.state;
    return AlertService.delete(alert.id)
      .then(() => {
        notification.success("Alert deleted successfully.");
        navigateTo("alerts");
      })
      .catch(() => {
        notification.error("Failed deleting alert.");
      });
  };

  archive = () => {
    const { alert } = this.state;
    alert.is_archived = true;
    return AlertService.save(alert)
      .then(() => {
        this.setState({ alert });
        notification.success("Alert archived successfully.");
        navigateTo(`alerts/archive`);
      })
      .catch(() => {
        notification.error("Failed archiving alert.");
      });
  };

  unarchive = () => {
    const { alert } = this.state;
    alert.is_archived = false;
    return AlertService.save(alert)
      .then(() => {
        this.setState({ alert });
        notification.success("Alert unarchived successfully.");
        navigateTo(`alerts/${alert.id}/edit`, true);
        this.setState({ mode: MODES.EDIT});
      })
      .catch(() => {
        notification.error("Failed unarchiving alert.");
      });
  };

  mute = () => {
    const { alert } = this.state;
    return AlertService.mute(alert)
      .then(() => {
        this.setAlertOptions({ muted: true });
        notification.warn("Notifications have been muted.");
      })
      .catch(() => {
        notification.error("Failed muting notifications.");
      });
  };

  unmute = () => {
    const { alert } = this.state;
    return AlertService.unmute(alert)
      .then(() => {
        this.setAlertOptions({ muted: false });
        notification.success("Notifications have been restored.");
      })
      .catch(() => {
        notification.error("Failed restoring notifications.");
      });
  };

  edit = () => {
    const { id } = this.state.alert;
    navigateTo(`alerts/${id}/edit`, true);
    this.setState({ mode: MODES.EDIT });
  };

  cancel = () => {
    const { id } = this.state.alert;
    navigateTo(`alerts/${id}/overview`, true);
    this.setState({ mode: MODES.OVERVIEW});
  };

  favorite = () => {
    const { id } = this.state.alert;
    return AlertService.favorite(id)
      .then(() => {
        notification.success("Alert added to favorites.");
      })
      .catch(() => {
        notification.error("Failed adding alert to favorites.");
      });
  };

  unfavorite = () => {
    const { id } = this.state.alert;
    return AlertService.unfavorite(id)
      .then(() => {
        //this.unfavorite()
        notification.success("Alert removed from favorites.");
      })
      .catch(() => {
        notification.error("Failed removing alert from favorites.");
      });
  };

  favorite_event = (id) => {
    return AlertEvent.favorite(id)
      .then(() => {
        notification.success("Event added to favorites.");
      })
      .catch(() => {
        notification.error("Failed adding event to favorites.");
      });
  };

  unfavorite_event = (id) => {
    return AlertEvent.unfavorite(id)
      .then(() => {
        //this.unfavorite()
        notification.success("Event removed from favorites.");
      })
      .catch(() => {
        notification.error("Failed removing event from favorites.");
      });
  };

  setAlertTags = obj => {
    if (!Array.isArray(obj)) {
        console.error("Expected an array for tags, but received:", obj);
        return;
    }

    this.setState(prevState => ({
        alert: { ...prevState.alert, tags: obj }
    }), this.save); // Ensure save is called after state update
};

  render() {
    const { alert, events } = this.state;
    if (!alert) {
      return <LoadingState className="m-t-30" />;
    }

    const muted = !!alert.options.muted;
    const { queryResult, mode, canEdit, pendingRearm } = this.state;

    const menuButton = (
      <MenuButton 
        doDelete={this.delete} 
        muted={muted} 
        mute={this.mute} 
        unmute={this.unmute} 
        canEdit={canEdit} 
        doArchive={this.archive} 
        doUnarchive={this.unarchive} 
        archived={alert.is_archived} 
        />
    );
    
    const enhancedAlert = {
      ...alert,
      favorite: this.favorite,
      unfavorite: this.unfavorite,
      is_favorite: alert.is_favorite,
      tags: alert.tags,
      query: alert.query,
      rearm: alert.rearm,
    };

    const commonProps = {
      alert: enhancedAlert,
      queryResult,
      pendingRearm,
      save: this.save,
      setAlertTags: this.setAlertTags,
      menuButton,
      onQuerySelected: this.onQuerySelected,
      onRearmChange: this.onRearmChange,
      onNameChange: this.onNameChange,
      onCriteriaChange: this.setAlertOptions,
      onNotificationTemplateChange: this.setAlertOptions,
      onChange: () => {},
    };

    return (
      <div className="alert-page">
        <div className="container">
          {mode === MODES.NEW && <AlertNew {...commonProps} />}
          {mode === MODES.VIEW && (
            <AlertView canEdit={canEdit} onEdit={this.edit} muted={muted} unmute={this.unmute} {...commonProps} />
          )}
          {mode === MODES.EDIT && <AlertEdit cancel={this.cancel} {...commonProps} />}
          {mode === MODES.FEVENTS && <AlertFevents alertId={alert.id} events={events} />}
          {mode === MODES.STATS && <AlertStats alertId={alert.id} events={events} />}
          {mode === MODES.OVERVIEW && (
            <>
              <AlertView canEdit={canEdit} onEdit={this.edit} muted={muted} unmute={this.unmute} {...commonProps} />
              <AlertStats alertId={alert.id} events={events} />
              <AlertFevents alertId={alert.id} events={events} />
            </>
          )}
        </div>
      </div>
    );
  }
}

routes.register(
  "Alerts.New",
  routeWithUserSession({
    path: "/alerts/new",
    title: "New Alert",
    render: pageProps => <Alert {...pageProps} mode={MODES.NEW} />,
  })
);
routes.register(
  "Alerts.View",
  routeWithUserSession({
    path: "/alerts/:alertId",
    title: "Alert",
    render: pageProps => <Alert {...pageProps} mode={MODES.VIEW} />,
  })
);
routes.register(
  "Alerts.Edit",
  routeWithUserSession({
    path: "/alerts/:alertId/edit",
    title: "Alert",
    render: pageProps => <Alert {...pageProps} mode={MODES.EDIT} />,
  })
);
routes.register(
  "Alerts.Fevents",
  routeWithUserSession({
    path: "/alerts/:alertId/fevents",
    title: "Alert Filtered Events",
    render: pageProps => <Alert {...pageProps} mode={MODES.FEVENTS} />,
  })
);
routes.register(
  "Alerts.Stats",
  routeWithUserSession({
    path: "/alerts/:alertId/stats",
    title: "Alert Statistics",
    render: pageProps => <Alert {...pageProps} mode={MODES.STATS} />,
  })
);
routes.register(
  "Alerts.Overview",
  routeWithUserSession({
    path: "/alerts/:alertId/overview",
    title: "Alert Overview",
    render: pageProps => <Alert {...pageProps} mode={MODES.OVERVIEW} />,
  })
);
